import { useEffect, useRef } from "react";
import {
  BriefcaseIcon,
  CheckCircleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import OnboardingModal from "../components/OnboardingModal";
import OnboardingPrimaryButton from "../components/OnboardingPrimaryButton";
import { ONBOARDING_STEP_HEADING_ID } from "../components/OnboardingStepHeader";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingSurface } from "../state/useOnboardingSurface";

/**
 * Business branch — "We’ll be in touch!" (`16224:294787`).
 *
 * Reaching it means the backend accepted the claim; the form does not advance
 * otherwise. Not a counted step, so no "Step N of M".
 *
 * "Got it" carries on to "Let’s explore!", the city derived from the
 * business, which completes onboarding — @see `BusinessCityStep`.
 *
 * When the lookup classified the place as a category Sociability does not
 * list, the callout native draws (`15666:346376`) says the business is not
 * visible yet. The desktop frames have no variant for it; it is native's copy
 * in the same place.
 */
const ClaimSubmittedStep = () => {
  const i18n = useI18N();
  const { answers, goNext, dismiss, isEngaged } = useOnboardingFlow();
  const { presentation } = useOnboardingSurface();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const title = i18n.get("onboardingBusinessSubmittedTitle");
  const isNotVisible = answers.businessClaim?.business?.isSupported === false;

  // Same rule as `OnboardingStepHeader`, which this screen does not use: the
  // docked sheet only takes focus once the visitor is working through it.
  useEffect(() => {
    if (presentation !== "docked" || isEngaged) headingRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OnboardingModal
      onClose={dismiss}
      ariaLabel={title}
      footer={
        <OnboardingPrimaryButton
          label={i18n.get("onboardingBusinessGotIt")}
          onClick={goNext}
        />
      }
    >
      <div className="flex min-h-full flex-col items-center justify-center gap-4 text-center">
        {/* Decorative — the heading says the same thing. */}
        <span
          aria-hidden="true"
          className="relative flex h-[58px] w-[58px] items-center justify-center rounded-full border-[3.25px] border-border-token-brand-subtle bg-container-neutral-minimal"
        >
          <BriefcaseIcon
            size={24}
            weight="fill"
            className="text-fg-neutral-strong"
          />
          <span className="absolute -right-2 -top-2 rounded-full bg-container-neutral-white">
            <CheckCircleIcon
              size={22}
              weight="fill"
              className="text-fg-brand-subtle"
            />
          </span>
        </span>

        <div className="flex flex-col gap-2">
          <h2
            ref={headingRef}
            id={ONBOARDING_STEP_HEADING_ID}
            tabIndex={-1}
            className="title-l-bold text-fg-neutral-strong outline-none"
          >
            {title}
          </h2>
          <p className="body-m-medium text-fg-neutral-subtle">
            {i18n.get("onboardingBusinessSubmittedBody")}
          </p>
        </div>

        {isNotVisible ? (
          <div className="flex w-full items-start gap-2 rounded-lg bg-container-brand-minimal px-4 py-3 text-left">
            <WarningCircleIcon
              size={24}
              aria-hidden="true"
              className="shrink-0 text-fg-brand-subtle"
            />
            <div className="flex flex-col px-1">
              <p className="body-l-semibold text-fg-neutral-strong">
                {i18n.get("onboardingBusinessNotVisibleTitle")}
              </p>
              <p className="body-s-regular text-fg-neutral-subtle">
                {i18n.get("onboardingBusinessNotVisibleBody")}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </OnboardingModal>
  );
};

export default ClaimSubmittedStep;
